import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import {
  reportHistoryHeaders,
  mdReportHistoryHeaders,
  smReportHistoryHeaders,
} from "./issueHeaders";
import { FAB } from "react-native-paper";
import {
  setIssueCategoryFilter,
  setIssuePage,
  setIssueSearchQuery,
  setIssueType,
} from "../../../redux/reducers/portalReducer";
import { getPortalReportHistory } from "../../../redux/dispatcher/portalDispatcher";
import { DynamicLabelBannerWithSearchBar } from "../../../components/custom/Banners/DynamicLabelBannerWithSearchBar";
import { PortalNestedDataTable } from "../../../components/custom/DataTable/PortalNestedDataTable";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import Spinner from "../../../components/shared/Spinner";

export default function CustomerIssueScreen({ navigation }: any) {
  const isFocused = useIsFocused();
  const dispatch = useDispatch<any>();

  // Get portal state from Redux
  const {
    orders,
    loading,
    searching,
    totalCount,
    totalPages,
    page,
    pageSize,
    searchQuery,
    categoryFilter,
    sortBy,
    issueType,
  } = useSelector((state: any) => state.portal.issues);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      reportHistoryHeaders,
    );

  const { isMobileDevice, isSmallDevice, isMediumDevice, isLargeDevice } =
    useContext(DeviceOrientationContext);

  const STATUS_FILTERS = ["all", "OPEN", "CLOSE"];

  const ISSUE_TYPE_FILTERS = [
    "all",
    "DAMAGED_ITEM",
    "MISSING_ITEM",
    "WRONG_ITEM",
    "CANCEL_ORDER",
    "OTHER",
  ];

  // Display label: strip underscores for UI, keep raw value for dispatch
  const formatFilterLabel = (value: string) =>
    value === "all" ? "All" : value.replace(/_/g, " ");

  // Separate local state for each filter group
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedIssueTypes, setSelectedIssueTypes] = useState<string[]>([]);

  const handleStatusChange = (option: string) => {
    if (option === "all") {
      setSelectedStatuses([]);
      dispatch(setIssueCategoryFilter("all"));
    } else {
      const updated = selectedStatuses.includes(option)
        ? selectedStatuses.filter((s) => s !== option)
        : [...selectedStatuses, option];
      setSelectedStatuses(updated);
      dispatch(
        setIssueCategoryFilter(
          updated.length === 0 ? "all" : updated.join(","),
        ),
      );
    }
    dispatch(setIssuePage(1));
  };

  const handleIssueTypeChange = (option: string) => {
    if (option === "all") {
      setSelectedIssueTypes([]);
      dispatch(setIssueType("all"));
    } else {
      const updated = selectedIssueTypes.includes(option)
        ? selectedIssueTypes.filter((t) => t !== option)
        : [...selectedIssueTypes, option];
      setSelectedIssueTypes(updated);
      dispatch(setIssueType(updated.length === 0 ? "all" : updated.join(",")));
    }
    dispatch(setIssuePage(1));
  };

  const renderFilters = () => (
    <View style={styles.quickFilterBar}>
      {/* Status row */}
      <View style={styles.filterRow}>
        <Text style={styles.quickFilterLabel}>Status:</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {STATUS_FILTERS.map((option) => {
            const isActive =
              option === "all"
                ? selectedStatuses.length === 0
                : selectedStatuses.includes(option);
            return (
              <TouchableOpacity
                key={option}
                style={[styles.filterChip, isActive && styles.filterChipActive]}
                onPress={() => handleStatusChange(option)}
              >
                <Text
                  style={
                    isActive
                      ? styles.filterChipTextActive
                      : styles.filterChipText
                  }
                >
                  {formatFilterLabel(option)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {/* Issue type row */}
      <View style={styles.filterRow}>
        <Text style={styles.quickFilterLabel}>Type{"   "}</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {ISSUE_TYPE_FILTERS.map((option) => {
            const isActive =
              option === "all"
                ? selectedIssueTypes.length === 0
                : selectedIssueTypes.includes(option);
            return (
              <TouchableOpacity
                key={option}
                style={[styles.filterChip, isActive && styles.filterChipActive]}
                onPress={() => handleIssueTypeChange(option)}
              >
                <Text
                  style={
                    isActive
                      ? styles.filterChipTextActive
                      : styles.filterChipText
                  }
                >
                  {formatFilterLabel(option)}
                </Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [fabPosition] = useState<number>(320);

  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };

  const navigateToOrder = (order: any, action: string) => {
    navigation.navigate("CustomerIssueDetailsReport", { orderId: order._id });
  };

  // Fetch orders on mount and when filters change
  useEffect(() => {
    if (isFocused) {
      dispatch(getPortalReportHistory() as any).unwrap();
    }
  }, [
    isFocused,
    dispatch,
    page,
    searchQuery,
    categoryFilter,
    sortBy,
    issueType,
  ]);

  // Update table headers based on device size
  useEffect(() => {
    setTableHeaders(
      isLargeDevice
        ? reportHistoryHeaders
        : isMediumDevice
          ? mdReportHistoryHeaders
          : smReportHistoryHeaders,
    );
  }, [isLargeDevice, isMediumDevice, isSmallDevice]);

  // Handle search
  const handleSearch = (query: string) => {
    dispatch(setIssueSearchQuery(query));
    dispatch(setIssuePage(0)); // reset page on search
  };

  const handleSearchTextChange = (text: string) => {
    dispatch(setIssueSearchQuery(text));
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    dispatch(setIssuePage(newPage));
  };

  // Handle refresh
  const handleRefresh = () => {
    dispatch(getPortalReportHistory() as any);
  };

  const columnsToNotHaveThousandSeparator: Array<string> = [
    "Packing #",
    "Hubspot ID",
    "Document Number",
    "Customer Number",
    "Order No",
    "Ticket No",
  ];

  return (
    <View style={styles.container}>
      {isSmallDevice || isMobileDevice ? (
        <>
          {/* Mobile: Search bar full width on top */}
          <DynamicLabelBannerWithSearchBar
            titleName={`Total Items: ${totalCount || 0}`}
            setRefresh={handleRefresh}
            dataToExport={orders}
            fileName="issuesHistory"
            sheetName="Issues"
            disableTitle={false}
            noBorder
            handleSearch={handleSearch}
            onSearchTextChange={handleSearchTextChange}
            _searchText={searchQuery}
          />
          {/* Mobile: Dropdown filter button */}
          {renderFilters()}
        </>
      ) : (
        /* Desktop: Quick Filter chips + search in one row */
        <View style={styles.quickFilterBar}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.quickFilterChipsContainer}
          >
            {renderFilters()}
          </ScrollView>
          <View style={styles.quickFilterSearchSection}>
            <DynamicLabelBannerWithSearchBar
              titleName={`Total Items: ${totalCount || 0}`}
              setRefresh={handleRefresh}
              dataToExport={orders}
              fileName="orderReport"
              sheetName="Orders"
              disableTitle={false}
              noBorder
              handleSearch={handleSearch}
              onSearchTextChange={handleSearchTextChange}
              _searchText={searchQuery}
            />
          </View>
        </View>
      )}

      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loading && !searching ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff" }}>
            <FAB
              icon="arrow-up"
              style={[
                styles.fab,
                {
                  top:
                    contentOffSet +
                    Dimensions.get("window").height -
                    fabPosition,
                },
              ]}
              onPress={() =>
                listRef.current?.scrollTo({ x: 0, y: 0, animated: true })
              }
              small
            />
            <PortalNestedDataTable
              itemsPerPage={pageSize}
              isNestedDataLoaded={!loading}
              nestedHeader={tableHeaders}
              nestedRows={orders}
              rowKeyValue={"documentNumber"}
              key="rowKeyValue"
              nestedColumnsToNotHaveThousandSeparator={
                columnsToNotHaveThousandSeparator
              }
              isDisplayTimeWithDate={false}
              handleNestedSortColumn={() => {}}
              disableHeader={false}
              operationActions={navigateToOrder}
              isDetail={false}
              setModalVisible={() => {}}
              handleRowClick={() => {}}
              setSelected={() => {}}
              // Server-side pagination props
              useServerPagination={true}
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              loading={searching}
              allowDefaultView={true}
            />
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  quickFilterBar: {
    flexDirection: "column",
    alignItems: "flex-start",
    backgroundColor: "#fff",
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  quickFilterChipsContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 12,
  },
  quickFilterLabel: {
    fontWeight: "700",
    fontSize: 13,
    color: "#374151",
    marginRight: 10,
  },
  quickFilterSearchSection: {
    flexShrink: 0,
    minWidth: 350,
  },
  mobileFilterWrapper: {
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700",
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  mobileChipRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: "#f8f9fa",
  },
  spinnerContainer: {
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
  filterChip: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    marginRight: 8,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  filterChipActive: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  filterChipText: {
    fontSize: 13,
    color: "#475569",
    fontWeight: "500" as const,
  },
  filterChipTextActive: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "600" as const,
  },
  filterRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
  },
});
